import { supabase } from "@/integrations/supabase/client";
import { spotifyTokenCache } from "@/lib/spotify-token-cache";
import type { MusicProvider, Track, PlayerState, MusicProviderConfig } from "@/types/music-provider";

export class SpotifyProvider implements MusicProvider {
  type = 'spotify' as const;
  name = 'Spotify';
  isAuthenticated = false;
  isReady = false;
  
  private player: any = null;
  private deviceId: string | null = null;
  private playerState: PlayerState = {
    isPlaying: false,
    currentTrack: null,
    position: 0,
    duration: 0,
    volume: 100
  };

  async login(): Promise<void> {
    const { data, error } = await supabase.functions.invoke('get-spotify-config');
    
    if (error) {
      console.error('Failed to get Spotify config:', error);
      throw new Error('Failed to load Spotify configuration');
    }
    
    if (!data || !data.clientId) {
      throw new Error('Invalid Spotify configuration received');
    }
    
    const config = data as MusicProviderConfig;
    
    const codeVerifier = this.generateRandomString(128);
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    
    sessionStorage.setItem('spotify_code_verifier', codeVerifier);
    
    const params = new URLSearchParams({
      client_id: config.clientId,
      response_type: 'code',
      redirect_uri: config.redirectUri,
      code_challenge_method: 'S256',
      code_challenge: codeChallenge,
      scope: 'streaming user-read-email user-read-private user-read-playback-state user-modify-playback-state'
    });
    
    window.location.href = `https://accounts.spotify.com/authorize?${params}`;
  }

  async logout(): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      // ✅ Limpar cache local
      await spotifyTokenCache.deleteToken(user.id);
      
      // ✅ Remover do Supabase
      await supabase.from('music_connections').delete()
        .eq('user_id', user.id).eq('provider', 'spotify');
    }
    this.isAuthenticated = false;
    this.isReady = false;
    if (this.player) {
      this.player.disconnect();
      this.player = null;
    }
  }

  async checkAuthStatus(): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // ✅ Verificar cache primeiro
    const cachedToken = await spotifyTokenCache.getToken(user.id);
    if (cachedToken) {
      this.isAuthenticated = true;
      return true;
    }

    // ✅ Se não houver cache, buscar do Supabase
    const { data } = await supabase
      .from('music_connections')
      .select('*')
      .eq('user_id', user.id)
      .eq('provider', 'spotify')
      .maybeSingle();

    if (data) {
      // ✅ Salvar no cache
      await spotifyTokenCache.setToken({
        userId: user.id,
        accessToken: data.access_token,
        expiresAt: data.expires_at,
        deviceId: data.device_id || undefined,
        metadata: data.metadata as Record<string, any> | undefined
      });
    }

    this.isAuthenticated = !!data;
    return this.isAuthenticated;
  }

  async play(trackId?: string): Promise<void> {
    if (!this.deviceId) throw new Error('Dispositivo não conectado');
    
    const accessToken = await this.getAccessToken();
    const endpoint = `https://api.spotify.com/v1/me/player/play?device_id=${this.deviceId}`;
    
    const response = await fetch(endpoint, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: trackId ? JSON.stringify({ uris: [`spotify:track:${trackId}`] }) : undefined
    });

    // Retry with refreshed token if unauthorized
    if (response.status === 401) {
      console.log('Token expired, refreshing...');
      await this.refreshTokenIfNeeded(true);
      const newToken = await this.getAccessToken();
      
      const retryResponse = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${newToken}`,
          'Content-Type': 'application/json'
        },
        body: trackId ? JSON.stringify({ uris: [`spotify:track:${trackId}`] }) : undefined
      });

      if (!retryResponse.ok) {
        throw new Error('Failed to play track after token refresh');
      }
    } else if (!response.ok) {
      throw new Error('Failed to play track');
    }
  }

  async pause(): Promise<void> {
    if (this.player) await this.player.pause();
  }

  async resume(): Promise<void> {
    if (this.player) await this.player.resume();
  }

  async next(): Promise<void> {
    if (this.player) await this.player.nextTrack();
  }

  async previous(): Promise<void> {
    if (this.player) await this.player.previousTrack();
  }

  async seek(positionMs: number): Promise<void> {
    if (this.player) await this.player.seek(positionMs);
  }

  async setVolume(volume: number): Promise<void> {
    if (this.player) await this.player.setVolume(volume / 100);
  }

  getPlayerState(): PlayerState {
    return this.playerState;
  }

  getCurrentTrack(): Track | null {
    return this.playerState.currentTrack;
  }

  async searchTracks(query: string, limit = 20): Promise<Track[]> {
    const { data } = await supabase.functions.invoke('spotify-search-tracks', {
      body: { query, limit }
    });
    return data?.tracks || [];
  }

  async getRecommendations(seedTracks: string[], targetBPM?: number): Promise<Track[]> {
    const { data } = await supabase.functions.invoke('spotify-search-tracks', {
      body: { seedTracks, targetBPM }
    });
    return data?.tracks || [];
  }

  private async getAccessToken(): Promise<string> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuário não autenticado');

    // ✅ Verificar cache primeiro
    const cachedToken = await spotifyTokenCache.getToken(user.id);
    if (cachedToken) {
      return cachedToken.accessToken;
    }

    // ✅ Verificar se token precisa refresh
    await this.refreshTokenIfNeeded();

    // ✅ Buscar do Supabase
    const { data } = await supabase
      .from('music_connections')
      .select('access_token, expires_at, device_id, metadata')
      .eq('user_id', user.id)
      .eq('provider', 'spotify')
      .maybeSingle();

    if (!data) throw new Error('Conexão não encontrada');
    
    // ✅ Salvar no cache
    await spotifyTokenCache.setToken({
      userId: user.id,
      accessToken: data.access_token,
      expiresAt: data.expires_at,
      deviceId: data.device_id || undefined,
      metadata: data.metadata as Record<string, any> | undefined
    });
    
    return data.access_token;
  }

  private async refreshTokenIfNeeded(forceRefresh = false): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // ✅ Verificar cache primeiro
    let expiresAt: Date;
    const cachedToken = await spotifyTokenCache.getToken(user.id);
    
    if (cachedToken) {
      expiresAt = new Date(cachedToken.expiresAt);
    } else {
      const { data: connection } = await supabase
        .from('music_connections')
        .select('expires_at')
        .eq('user_id', user.id)
        .eq('provider', 'spotify')
        .maybeSingle();

      if (!connection) return;
      expiresAt = new Date(connection.expires_at);
    }

    const now = new Date();
    const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);

    // Refresh if expired, expiring soon, or forced
    if (forceRefresh || expiresAt <= fiveMinutesFromNow) {
      console.log('Refreshing Spotify token...', { 
        expired: expiresAt <= now,
        expiringSoon: expiresAt <= fiveMinutesFromNow,
        forceRefresh 
      });

      const { data, error } = await supabase.functions.invoke('refresh-music-token', {
        body: { provider: 'spotify' }
      });

      if (error) {
        console.error('Failed to refresh token:', error);
        throw new Error('Token refresh failed');
      }

      // ✅ Atualizar cache com novo token
      if (data?.access_token && data?.expires_at) {
        await spotifyTokenCache.updateAccessToken(
          user.id,
          data.access_token,
          data.expires_at
        );
      }

      console.log('Token refreshed successfully');
    }
  }

  private generateRandomString(length: number): string {
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const values = crypto.getRandomValues(new Uint8Array(length));
    return values.reduce((acc, x) => acc + possible[x % possible.length], '');
  }

  private async generateCodeChallenge(verifier: string): Promise<string> {
    const data = new TextEncoder().encode(verifier);
    const hashed = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(hashed)))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }
}
